import { ForwardedRef, forwardRef, useCallback, useMemo } from 'react';
import { NodeProps } from 'reactflow';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import {
    faClock,
    faEnvelope,
    faList,
    faMobile,
    faNote,
    faPenField,
    faSplit,
    faTag,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChooseActionNodeData } from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { baseNodeDefaults } from 'assets/js/pages/engage/workflows/utils/constants';
import {
    useReactFlowStore,
    workflowHasActionSelector,
} from 'assets/js/pages/engage/workflows/utils/store';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import * as DropdownMenu from 'components/dropdown-menu';
import { WorkflowActionTypeEnum, WorkflowUtilityNodeTypeEnum } from 'constants/workflows';
import { first, last, some } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid } from 'uuid';

interface ChooseActionProps extends NodeProps<ChooseActionNodeData> {}
const ChooseAction = ({ id, selected }: ChooseActionProps) => {
    const { nodes, edges, setNodes, getNode, setLastAddedId } = useReactFlowStore(
        ({ nodes, edges, setNodes, getNode, setLastAddedId }) => ({
            nodes,
            edges,
            setNodes,
            getNode,
            setLastAddedId,
        }),
    );

    const workflowHasAction = useReactFlowStore(workflowHasActionSelector);
    const isAnyNodeSelected = useReactFlowStore(state => state.nodes.some(n => n.selected));

    const isError = !workflowHasAction && !isAnyNodeSelected;

    const shouldHideBranchAction = useMemo(() => {
        const childNode = nodes.find(n => n.data?.parentId === id);
        return childNode?.type && childNode.type !== WorkflowUtilityNodeTypeEnum.LEAF_END;
    }, [id, nodes]);

    const insertNewNodes = useCallback(
        (nodesToAppend: NodeWithData[]) => {
            if (nodesToAppend.length <= 0) return;

            const isBranchEnd = !edges.find(edge => edge.source === id);
            const lastAppendedNodeId = last(nodesToAppend)?.id!;
            let newNodes = nodes.filter(node => node.id !== id);

            if (isBranchEnd) {
                // this is the end of a branch
                // if it is not a BRANCH step, we need to insert LEAF_END node
                if (
                    !some(nodesToAppend, node => node.type === WorkflowUtilityNodeTypeEnum.BRANCH)
                ) {
                    nodesToAppend.push({
                        ...baseNodeDefaults,
                        id: uuid(),
                        type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                        data: {
                            type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                            parentId: lastAppendedNodeId,
                        },
                    });
                }
                newNodes = [...newNodes, ...nodesToAppend];
            } else {
                // this is adding between nodes, need to adjust parentIds
                newNodes = [
                    ...newNodes.map(node => {
                        if (node.data?.parentId !== id) return node;
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                parentId: lastAppendedNodeId,
                            },
                        };
                    }),
                    ...nodesToAppend,
                ];
            }

            setNodes(newNodes);
            setLastAddedId(first(nodesToAppend)?.id ?? null);
        },
        [nodes, edges, setNodes, id, setLastAddedId],
    );

    const onClickSendEmail = useCallback(() => {
        const sendEmailNodeId = uuid();
        const currentNode = getNode(id);

        if (!currentNode?.data) return;

        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: sendEmailNodeId,
                type: WorkflowActionTypeEnum.SEND_EMAIL,
                selected: true,
                data: {
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    const onClickSendSms = useCallback(() => {
        const sendSmsNodeId = uuid();
        const currentNode = getNode(id);

        if (!currentNode?.data) return;

        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: sendSmsNodeId,
                type: WorkflowActionTypeEnum.SEND_SMS,
                selected: true,
                data: {
                    type: WorkflowActionTypeEnum.SEND_SMS,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    const onClickTagContact = useCallback(() => {
        const currentNode = getNode(id);
        if (!currentNode?.data) return;

        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: uuid(),
                type: WorkflowActionTypeEnum.CONTACT_TAG,
                selected: true,
                data: {
                    type: WorkflowActionTypeEnum.CONTACT_TAG,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    const onClickAddNote = useCallback(() => {
        const currentNode = getNode(id);
        if (!currentNode?.data) return;

        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: uuid(),
                type: WorkflowActionTypeEnum.ADD_NOTE,
                selected: true,
                data: {
                    type: WorkflowActionTypeEnum.ADD_NOTE,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    const onClickCreateTask = useCallback(() => {
        const currentNode = getNode(id);
        if (!currentNode?.data) return;
        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: uuid(),
                type: WorkflowActionTypeEnum.TASK_CREATE,
                selected: true,
                data: {
                    type: WorkflowActionTypeEnum.TASK_CREATE,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    const onClickAddCustomField = useCallback(() => {
        const currentNode = getNode(id);
        if (!currentNode?.data) return;

        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: uuid(),
                type: WorkflowActionTypeEnum.SET_CUSTOM_FIELD,
                selected: true,
                data: {
                    type: WorkflowActionTypeEnum.SET_CUSTOM_FIELD,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    const onClickAddWait = useCallback(() => {
        const currentNode = getNode(id);
        if (!currentNode?.data) return;

        const delayNode = {
            ...baseNodeDefaults,
            id: uuid(),
            type: WorkflowUtilityNodeTypeEnum.DELAY,
            selected: true,
            data: {
                type: WorkflowUtilityNodeTypeEnum.DELAY,
                parentId: currentNode.data.parentId,
            },
        };

        const chooseActionNode = {
            ...baseNodeDefaults,
            id: uuid(),
            type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
            data: {
                type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
                parentId: delayNode.id,
            },
        };

        insertNewNodes([delayNode, ...(workflowHasAction ? [] : [chooseActionNode])]);
    }, [insertNewNodes, getNode, id, workflowHasAction]);

    const onClickAddBranch = useCallback(() => {
        const currentNode = getNode(id);
        if (!currentNode?.data) return;

        insertNewNodes([
            {
                ...baseNodeDefaults,
                id: uuid(),
                type: WorkflowUtilityNodeTypeEnum.BRANCH,
                selected: true,
                data: {
                    type: WorkflowUtilityNodeTypeEnum.BRANCH,
                    parentId: currentNode.data.parentId,
                },
            },
        ]);
    }, [insertNewNodes, getNode, id]);

    return (
        <CanvasNode.Root isSelected={selected} isError={isError} id={id}>
            <CanvasNode.Container>
                <CanvasNode.ErrorAlert isError={isError}>
                    <p className="max-w-40 text-sm">
                        You need at least one action in order to publish your workflow.
                    </p>
                </CanvasNode.ErrorAlert>
                <CanvasNode.Body>
                    {workflowHasAction && (
                        <DropdownMenu.Root>
                            <CanvasNode.ActionMenuTrigger />
                            <DropdownMenu.Content align="end">
                                <DeleteCanvasNodeMenuItem />
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    )}
                    <div className="flex flex-col gap-3">
                        <p className="text-sm font-medium text-gray-500">Rules</p>
                        <div className="flex min-w-0 flex-wrap gap-2">
                            <ActionButton
                                className="min-w-[45%] flex-grow"
                                onClick={onClickAddWait}
                            >
                                <FontAwesomeIcon
                                    icon={faClock}
                                    className="text-brand-violet-primary"
                                />
                                Wait
                            </ActionButton>
                            {!shouldHideBranchAction && (
                                <ActionButton
                                    className="min-w-[45%] flex-grow"
                                    onClick={onClickAddBranch}
                                >
                                    <FontAwesomeIcon
                                        icon={faSplit}
                                        className="text-brand-violet-primary rotate-90"
                                    />
                                    Branch
                                </ActionButton>
                            )}
                        </div>
                        <p className="text-sm font-medium text-gray-500">Actions</p>
                        <ActionButton onClick={onClickSendEmail}>
                            <FontAwesomeIcon icon={faEnvelope} className="text-green-500" />
                            Send email
                        </ActionButton>
                        <ActionButton onClick={onClickSendSms}>
                            <FontAwesomeIcon icon={faMobile} className="text-green-500" />
                            Send text
                        </ActionButton>
                        <ActionButton onClick={onClickCreateTask}>
                            <FontAwesomeIcon icon={faList} className="text-green-500" />
                            Create a task
                        </ActionButton>
                        <ActionButton onClick={onClickTagContact}>
                            <FontAwesomeIcon icon={faTag} className="text-green-500" />
                            Tag/untag contact
                        </ActionButton>
                        <ActionButton onClick={onClickAddNote}>
                            <FontAwesomeIcon icon={faNote} className="text-green-500" />
                            Add note to contact
                        </ActionButton>
                        <ActionButton onClick={onClickAddCustomField}>
                            <FontAwesomeIcon icon={faPenField} className="text-green-500" />
                            Set custom field
                        </ActionButton>
                    </div>
                </CanvasNode.Body>
            </CanvasNode.Container>
        </CanvasNode.Root>
    );
};

interface ActionButtonProps extends Omit<ButtonProps, 'variant'> {}
const ActionButton = forwardRef(
    ({ className, ...rest }: ActionButtonProps, forwardedRef: ForwardedRef<HTMLButtonElement>) => {
        return (
            <Button
                ref={forwardedRef}
                size="md"
                variant="outline"
                color="neutral"
                className={twMerge(
                    '!h-auto !justify-start !border-gray-200 !py-3 hover:!border-gray-300 hover:!bg-white hover:shadow',
                    className,
                )}
                {...rest}
            />
        );
    },
);

export { ChooseAction };
